import request from "../request";
import {
  bugsnagPostgrestErrorHandler,
  recursiveCamelCaseCipher,
  snakeCaseCipher,
  formatDataObj,
  formatDataArray,
} from "./common";

import type {
  DesignDoc,
  DocumentGroup,
  DesignDocGrouping,
  DesignDocNotation,
  DesignDocCategoryCompleteness,
  DesignDocAPIType,
} from "../../types";
import { CheckFeatureAccessHeader } from "@src/auth/permissions/usePreRequestFeatureAccessCheck";

// design doc notes come back in following format from postgrest api
// {id: number, note: string }[] -- Where the note is stringified JSON
// equipment docs return note in valid json by default
// So we need to parse the json string to get the notes in the right format for design docs
export function formatNoteAggr(
  notes: { id: number; note: string }[] | null | undefined
): DesignDoc["noteAggr"] {
  return notes
    ? notes.map((note) => {
        const { note: _note, ...rest } = note;
        return {
          ...rest,
          description: JSON.parse(_note).description as string,
          page: JSON.parse(_note).page as string,
        };
      })
    : [];
}

// endpoint requires {feature: "Documents", access: "read"}
export const getDesignDocs = (
  siteId: number,
  checkFeatureAccessHeader: CheckFeatureAccessHeader
): Promise<Array<DesignDoc>> => {
  return request
    .get(`/rest/v_design_docs`)
    .set(checkFeatureAccessHeader)
    .query({
      site_id: `eq.${siteId}`,
      status: `neq.deleted`,
      order: "id.asc",
    })
    .then(async ({ body }) => {
      const designDocs = recursiveCamelCaseCipher(body) as DesignDocAPIType[];
      return designDocs.map((doc) => ({
        ...doc,
        noteAggr: formatNoteAggr(doc.noteAggr),
      }));
    });
};

// endpoint requires {feature: "Documents", access: "edit"}
export const updateDesignDocs = (
  docId: string | number,
  props: Partial<DesignDoc>,
  checkFeatureAccessHeader: CheckFeatureAccessHeader
): Promise<void> =>
  request
    .patch(`/rest/design_docs`)
    .set("Prefer", "return=representation")
    .set(checkFeatureAccessHeader)
    .query({ id: `eq.${docId}` })
    .send(formatDataObj(props, snakeCaseCipher))
    .then(({ body }) => recursiveCamelCaseCipher(body));

// endpoint requires {feature: "Documents", access: "edit"}
export const postDesignDocNotations = (
  newNotation: Partial<DesignDocNotation>,
  checkFeatureAccessHeader: CheckFeatureAccessHeader
): Promise<Array<DesignDocNotation>> =>
  request
    .post(`/rest/design_doc_notations`)
    .set("Prefer", "return=representation")
    .set(checkFeatureAccessHeader)
    .send(formatDataObj(newNotation, snakeCaseCipher))
    .then(({ body }) => recursiveCamelCaseCipher(body));

// endpoint requires {feature: "Documents", access: "edit"}
export const updateDesignDocNotations = (
  noteId: string | number,
  props: Partial<DesignDocNotation>,
  checkFeatureAccessHeader: CheckFeatureAccessHeader
): Promise<void> =>
  request
    .patch(`/rest/design_doc_notations`)
    .set("Prefer", "return=representation")
    .set(checkFeatureAccessHeader)
    .query({ id: `eq.${noteId}` })
    .send(formatDataObj(props, snakeCaseCipher))
    .then(({ body }) => recursiveCamelCaseCipher(body));

// endpoint requires {feature: "Documents", access: "edit"}
export const deleteDesignDocNotations = (
  id: string | number,
  checkFeatureAccessHeader: CheckFeatureAccessHeader
): Promise<void> =>
  request
    .delete(`/rest/design_doc_notations`)
    .set("Prefer", "return=representation")
    .set(checkFeatureAccessHeader)
    .query({ id: `eq.${id}` })
    .then(({ body }) => recursiveCamelCaseCipher(body));

// endpoint requires {feature: "Documents", access: "read"}
export const getDesignDocGroups = (
  siteId: number,
  checkFeatureAccessHeader: CheckFeatureAccessHeader
): Promise<Array<DocumentGroup>> => {
  return request
    .get(`/rest/design_doc_groups`)
    .set(checkFeatureAccessHeader)
    .query({ site_id: `eq.${siteId}`, order: "id.asc" })
    .then(async ({ body }) => recursiveCamelCaseCipher(body));
};

// endpoint requires {feature: "Documents", access: "edit"}
export const postDesignDocGroups = (
  newGroup: Partial<DocumentGroup>,
  checkFeatureAccessHeader: CheckFeatureAccessHeader
): Promise<Array<DocumentGroup>> =>
  request
    .post(`/rest/design_doc_groups`)
    .set("Prefer", "return=representation")
    .set(checkFeatureAccessHeader)
    .send(formatDataObj(newGroup, snakeCaseCipher))
    .then(({ body }) => recursiveCamelCaseCipher(body));

// endpoint requires {feature: "Documents", access: "edit"}
export const patchDesignDocGroups = (
  { id, groupname }: Pick<DocumentGroup, "id" | "groupname">,
  checkFeatureAccessHeader: CheckFeatureAccessHeader
) =>
  request
    .patch(`/rest/design_doc_groups`)
    .set(checkFeatureAccessHeader)
    .query({ id: `eq.${id}` })
    .send({ groupname });

// endpoint requires {feature: "Documents", access: "edit"}
export const postDesignDocGrouping = (
  newGrouping: Partial<DesignDocGrouping>,
  checkFeatureAccessHeader: CheckFeatureAccessHeader
): Promise<Array<DesignDocGrouping>> =>
  request
    .post(`/rest/design_doc_groupings`)
    .set(checkFeatureAccessHeader)
    .set("Prefer", "return=representation")
    .send(formatDataObj(newGrouping, snakeCaseCipher))
    .then(({ body }) => recursiveCamelCaseCipher(body));

// endpoint requires {feature: "Documents", access: "edit"}
export const deleteAllFileGroupings = (
  fileId: number | string,
  checkFeatureAccessHeader: CheckFeatureAccessHeader
): Promise<any> =>
  request
    .delete(`/rest/design_doc_groupings`)
    .set(checkFeatureAccessHeader)
    .set("Prefer", "return=representation")
    .query({ file_id: `eq.${fileId}` });

// endpoint requires {feature: "Documents", access: "read"}
export const getDesignDocCategoryCompleteness = (
  siteId: string | number,
  checkFeatureAccessHeader: CheckFeatureAccessHeader
): Promise<Array<DesignDocCategoryCompleteness> | undefined> =>
  request
    .get(`/rest/design_doc_category_completeness`)
    .set(checkFeatureAccessHeader)
    .query({ site_id: `eq.${siteId}` })
    .then(({ body }) => recursiveCamelCaseCipher(body))
    .catch((error) => bugsnagPostgrestErrorHandler(error));

// endpoint requires {feature: "Documents", access: "edit"}
export const postDesignDocCategoryCompleteness = (
  designDocCompleteness: Array<Partial<DesignDocCategoryCompleteness>>,
  checkFeatureAccessHeader: CheckFeatureAccessHeader
): Promise<void> =>
  request
    .post(`/rest/design_doc_category_completeness`)
    .set(checkFeatureAccessHeader)
    .send(formatDataArray(designDocCompleteness, snakeCaseCipher))
    .then(({ body }) => recursiveCamelCaseCipher(body))
    .catch((error) => bugsnagPostgrestErrorHandler(error));

// endpoint requires {feature: "Documents", access: "edit"}
export const patchDesignDocCategoryCompleteness = (
  designDocCompleteness: Array<Partial<DesignDocCategoryCompleteness>>,
  checkFeatureAccessHeader: CheckFeatureAccessHeader
) =>
  Promise.all(
    designDocCompleteness.map(async ({ id, ...completeness }) => {
      return await request
        .patch(`/rest/design_doc_category_completeness`)
        .set(checkFeatureAccessHeader)
        .query({ id: `eq.${id}` })
        .send(formatDataObj({ ...completeness }, snakeCaseCipher))
        .then(({ body }) => recursiveCamelCaseCipher(body))
        .catch((error) => bugsnagPostgrestErrorHandler(error));
    })
  );
